import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  Cable,
  ExternalLink,
  Filter,
  ChevronDown,
  ChevronsUpDown,
  ChevronsDownUp,
} from 'lucide-react';

export type EndpointStatus = 'integrated' | 'planned' | 'not_applicable';
export type EndpointPurpose =
  | 'subscription'
  | 'charge'
  | 'payment'
  | 'cancel'
  | 'other';

export interface ChannelEndpoint {
  id: string;
  channelSlug: string;
  provider: string;
  method: string;
  path: string;
  purpose: EndpointPurpose;
  status: EndpointStatus;
  notes?: string;
  docsUrl?: string;
}

interface EndpointsSummary {
  total: number;
  integrated: number;
  planned: number;
  not_applicable: number;
  byChannel: Record<
    string,
    { integrated: number; planned: number; not_applicable: number; total: number }
  >;
}

const STATUS_BADGE: Record<EndpointStatus, string> = {
  integrated: 'badge badge-success',
  planned: 'badge badge-info',
  not_applicable: 'badge badge-muted',
};

const STATUS_LABEL: Record<EndpointStatus, string> = {
  integrated: 'Integrado',
  planned: 'Planificado',
  not_applicable: 'N/A',
};

const PURPOSE_LABEL: Record<EndpointPurpose, string> = {
  subscription: 'Suscripción',
  charge: 'Cargo',
  payment: 'Pago único',
  cancel: 'Cancelación',
  other: 'Otro',
};

const METHOD_CLASS: Record<string, string> = {
  GET: 'bg-sky-100 text-sky-900 border-sky-200 dark:bg-sky-500/20 dark:text-sky-200 dark:border-sky-500/30',
  POST: 'bg-emerald-100 text-emerald-900 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/30',
  PUT: 'bg-amber-100 text-amber-950 border-amber-200 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-500/30',
  PATCH: 'bg-orange-100 text-orange-950 border-orange-200 dark:bg-orange-500/20 dark:text-orange-100 dark:border-orange-500/30',
  DELETE: 'bg-rose-100 text-rose-950 border-rose-200 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/30',
};

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  };
}

function EndpointRow({ ep, index }: { ep: ChannelEndpoint; index: number }) {
  return (
    <li>
      <article
        className={`rounded-lyrax border border-lyrax-border bg-lyrax-bg/60 px-3.5 py-3 flex flex-col sm:flex-row sm:items-start justify-between gap-3 animate-fade-up stagger-${Math.min(index + 1, 4)}`}
      >
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-mono font-semibold ${METHOD_CLASS[ep.method] || METHOD_CLASS.GET}`}
            >
              {ep.method}
            </span>
            <code className="text-sm font-mono text-lyrax-text break-all">{ep.path}</code>
            <span className={STATUS_BADGE[ep.status]}>{STATUS_LABEL[ep.status]}</span>
            <span className="badge badge-muted">{PURPOSE_LABEL[ep.purpose] || ep.purpose}</span>
          </div>
          {ep.notes && <p className="meta-text max-w-3xl">{ep.notes}</p>}
        </div>
        {ep.docsUrl && (
          <a
            href={ep.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="icon-btn shrink-0 self-start"
            aria-label={`Documentación ${ep.path}`}
          >
            <ExternalLink className="w-4 h-4" aria-hidden />
          </a>
        )}
      </article>
    </li>
  );
}

function ChannelCollapse({
  slug,
  provider,
  items,
  channelSummary,
  open,
  onToggle,
}: {
  slug: string;
  provider: string;
  items: ChannelEndpoint[];
  channelSummary?: {
    integrated: number;
    planned: number;
    not_applicable: number;
    total: number;
  };
  open: boolean;
  onToggle: () => void;
}) {
  const panelId = `channel-panel-${slug}`;
  const headerId = `channel-header-${slug}`;

  return (
    <section className="card overflow-hidden !p-0">
      <h2 className="sr-only" id={headerId}>
        {provider}
      </h2>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-lyrax-bg/80 transition-colors"
        aria-expanded={open}
        aria-controls={panelId}
      >
        <ChevronDown
          className={`w-4 h-4 shrink-0 text-lyrax-text-secondary transition-transform duration-200 ${
            open ? 'rotate-0' : '-rotate-90'
          }`}
          aria-hidden
        />
        <span
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lyrax bg-lyrax-primary/15 text-emerald-800 dark:text-emerald-300"
          aria-hidden
        >
          <Cable className="w-4 h-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-lyrax-text">{provider}</span>
            <code className="text-xs font-mono text-lyrax-text bg-lyrax-bg border border-lyrax-border px-2 py-0.5 rounded">
              {slug}
            </code>
            <span className="meta-text tabular-nums">{items.length} endpoint{items.length === 1 ? '' : 's'}</span>
          </div>
          {channelSummary && (
            <p className="meta-text mt-0.5">
              {channelSummary.integrated} integrados · {channelSummary.planned} planificados
              {channelSummary.not_applicable > 0
                ? ` · ${channelSummary.not_applicable} N/A`
                : ''}
            </p>
          )}
        </div>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        hidden={!open}
        className={open ? 'border-t border-lyrax-border' : undefined}
      >
        {open && (
          <ul className="space-y-2 p-3 sm:p-4" aria-label={`Endpoints ${slug}`}>
            {items.map((ep, idx) => (
              <EndpointRow key={ep.id} ep={ep} index={idx} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export default function EndpointsIntegracionPage() {
  const [endpoints, setEndpoints] = useState<ChannelEndpoint[]>([]);
  const [summary, setSummary] = useState<EndpointsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channelFilter, setChannelFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [openChannels, setOpenChannels] = useState<Record<string, boolean>>({});

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/super-admin/channel-endpoints', {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Error al cargar endpoints de integración');
      const json = await res.json();
      const data: ChannelEndpoint[] = json.data || [];
      setEndpoints(data);
      setSummary(json.summary || null);

      const slugs = Array.from(new Set(data.map((e) => e.channelSlug))).sort();
      const initial: Record<string, boolean> = {};
      slugs.forEach((slug, i) => {
        initial[slug] = i === 0;
      });
      setOpenChannels(initial);
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const channels = useMemo(() => {
    return Array.from(new Set(endpoints.map((e) => e.channelSlug))).sort();
  }, [endpoints]);

  const filtered = useMemo(() => {
    return endpoints.filter((e) => {
      if (channelFilter !== 'all' && e.channelSlug !== channelFilter) return false;
      if (statusFilter !== 'all' && e.status !== statusFilter) return false;
      return true;
    });
  }, [endpoints, channelFilter, statusFilter]);

  const grouped = useMemo(() => {
    const map = new Map<string, ChannelEndpoint[]>();
    for (const e of filtered) {
      const list = map.get(e.channelSlug) || [];
      list.push(e);
      map.set(e.channelSlug, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  useEffect(() => {
    if (channelFilter !== 'all') {
      setOpenChannels((prev) => ({ ...prev, [channelFilter]: true }));
    }
  }, [channelFilter]);

  const expandAll = () => {
    const next: Record<string, boolean> = {};
    grouped.forEach(([slug]) => {
      next[slug] = true;
    });
    setOpenChannels((prev) => ({ ...prev, ...next }));
  };

  const collapseAll = () => {
    const next: Record<string, boolean> = {};
    grouped.forEach(([slug]) => {
      next[slug] = false;
    });
    setOpenChannels((prev) => ({ ...prev, ...next }));
  };

  const toggleChannel = (slug: string) => {
    setOpenChannels((prev) => ({ ...prev, [slug]: !prev[slug] }));
  };

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-20 meta-text"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="w-6 h-6 animate-spin mr-2 text-lyrax-primary" aria-hidden />
        Cargando endpoints…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-700 dark:text-red-300 py-10" role="alert">
        <AlertCircle className="w-5 h-5 shrink-0" aria-hidden />
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="label-caps text-lyrax-primary mb-2">Integraciones</p>
        <h1 className="page-title">Endpoints de integración</h1>
        <p className="page-subtitle max-w-2xl">
          Catálogo informativo (solo lectura) de paths HTTP que Lyrax consume por canal.
          Distingue lo ya cableado de lo planificado para charges/payments.
        </p>
      </header>

      {summary && (
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3" aria-label="Resumen">
          <div className="card py-3 px-4">
            <dt className="meta-text">Total</dt>
            <dd className="text-xl font-semibold text-lyrax-text tabular-nums">{summary.total}</dd>
          </div>
          <div className="card py-3 px-4">
            <dt className="meta-text">Integrados</dt>
            <dd className="text-xl font-semibold text-emerald-700 dark:text-emerald-300 tabular-nums">
              {summary.integrated}
            </dd>
          </div>
          <div className="card py-3 px-4">
            <dt className="meta-text">Planificados</dt>
            <dd className="text-xl font-semibold text-sky-800 dark:text-sky-300 tabular-nums">
              {summary.planned}
            </dd>
          </div>
          <div className="card py-3 px-4">
            <dt className="meta-text">N/A</dt>
            <dd className="text-xl font-semibold text-lyrax-text-secondary tabular-nums">
              {summary.not_applicable}
            </dd>
          </div>
        </dl>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="filter-channel" className="field-label flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5" aria-hidden />
              Canal
            </label>
            <select
              id="filter-channel"
              className="field-input min-w-[10rem]"
              value={channelFilter}
              onChange={(e) => setChannelFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              {channels.map((slug) => (
                <option key={slug} value={slug}>
                  {slug}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="filter-status" className="field-label">
              Estado
            </label>
            <select
              id="filter-status"
              className="field-input min-w-[10rem]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="integrated">Integrado</option>
              <option value="planned">Planificado</option>
              <option value="not_applicable">N/A</option>
            </select>
          </div>
        </div>

        {grouped.length > 0 && (
          <div className="flex items-center gap-2">
            <button type="button" onClick={expandAll} className="btn-secondary text-sm !py-2 !px-3">
              <ChevronsUpDown className="w-3.5 h-3.5 mr-1.5 inline" aria-hidden />
              Expandir
            </button>
            <button type="button" onClick={collapseAll} className="btn-secondary text-sm !py-2 !px-3">
              <ChevronsDownUp className="w-3.5 h-3.5 mr-1.5 inline" aria-hidden />
              Colapsar
            </button>
          </div>
        )}
      </div>

      {grouped.length === 0 ? (
        <p className="meta-text py-8">Sin endpoints para el filtro actual.</p>
      ) : (
        <div className="space-y-3">
          {grouped.map(([slug, items]) => (
            <ChannelCollapse
              key={slug}
              slug={slug}
              provider={items[0]?.provider || slug}
              items={items}
              channelSummary={summary?.byChannel[slug]}
              open={!!openChannels[slug]}
              onToggle={() => toggleChannel(slug)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
